import React from 'react'
import { View } from 'react-native'
import { OptionColor } from '@order/steps/types'
import Typography from '@ui/typography'
import { colors as c, colors } from '@ui/colors'
import { optionGradient, optionTextColor } from './styles'
import Gradient from '@ui/effects/Gradient'
import { useTranslation } from 'react-i18next'
import { useCurrencyDecimals, useCurrencySign } from 'hooks/useCurrency'
import { DEFAULT_DECIMALS } from '@ca/common'
import cn from '@ui/utils/cn'
import { leadingNone } from '@ui/typography/styles'
import { Discountable } from '@order/utils/discounts/Discounts'
import { sPricePrefix } from '@/price/PriceInfo/styles'
import { GradientType } from '@ui/types'

interface Props {
  price?: Discountable
  prefix?: string | null
  suffix?: string | null
  priceValue?: string | null
  colorOption?: OptionColor
  color?: string
  text?: string // overrides "included" text
  hideDiscountPrefix?: boolean
}

export const formatPrice = (value: number, decimals = DEFAULT_DECIMALS) =>
  value ? (value % 1 === 0 ? value.toFixed(0) : value.toFixed(decimals)) : value

export default function PricePackage({
  price = { base: 0 },
  prefix,
  suffix,
  colorOption,
  color,
  text,
  priceValue,
  hideDiscountPrefix = false,
}: Props) {
  const { t } = useTranslation(['steps', 'ui-steps', 'common'])
  const currencySign = useCurrencySign()
  const decimals = useCurrencyDecimals()
  const isDiscounted = price.applied && price.applied.length > 0
  const value = isDiscounted ? price.discounted : price.base
  const isIncluded = !value
  const isPlusSign = !isIncluded && !prefix

  const suffixText = suffix
    ? t(`steps:priceSuffix.${suffix}.short`, suffix)
    : null

  const colorText = color
    ? color
    : colorOption
    ? optionTextColor[colorOption]
    : optionTextColor['default']

  const colorGradient = color
    ? ([color, color] satisfies GradientType)
    : colorOption
    ? optionGradient[colorOption]
    : optionGradient['default']

  return (
    <View className="flex flex-col items-center justify-center gap-1">
      {prefix && !isDiscounted && !isIncluded && (
        <Typography variant="SMB" className={sPricePrefix} color={colorText}>
          {t(`steps:pricePrefix.${prefix}`)}
        </Typography>
      )}
      {!hideDiscountPrefix && isDiscounted && !isIncluded && (
        <View
          className={cn(
            sPricePrefix,
            'flex flex-row flex-nowrap items-center gap-[5px]'
          )}
        >
          <Typography variant="SMB" color={colorText}>
            {t(`ui-steps:pricePrefix.discount.start`)}
          </Typography>
          <View className="relative">
            <View
              className="absolute w-full rounded-full bg-red-500"
              style={{
                height: 2,
                top: 9,
                transform: [{ scaleX: 1.25 }, { rotate: '-30deg' }],
              }}
            />
            <Typography
              variant="SMB"
              color={color ? colorOption : colors.text}
              className="text-nowrap"
            >
              {`${formatPrice(price.base, decimals)}${currencySign}`}
            </Typography>
          </View>
          <Typography variant="SMB" color={colorText}>
            {t(`ui-steps:pricePrefix.discount.end`)}
          </Typography>
        </View>
      )}
      <View className="relative inset-0 h-10 justify-center rounded-2xl">
        {isIncluded && (
          <Gradient
            className="rounded-2xl opacity-20"
            colors={[colorText, colorText]}
          />
        )}
        {!isIncluded && (
          <Gradient
            className="rounded-2xl"
            colors={colorGradient}
            start={[0.06, 0.06]}
            end={[1, 1]}
          />
        )}
        {isIncluded && (
          <Typography
            variant="MDB"
            className={cn('px-6 uppercase', leadingNone)}
            color={colorText}
          >
            {priceValue || text || t('common:included')}
          </Typography>
        )}
        {!isIncluded && (
          <View className="flex flex-row items-end justify-center gap-1 px-6">
            <Typography
              variant="LGB"
              className={cn('uppercase tracking-[0]', leadingNone)}
              color={c.white}
            >
              {isPlusSign && !priceValue ? '+ ' : ''}
              {priceValue || `${formatPrice(value, decimals)}${currencySign}`}
            </Typography>
            {suffixText && (
              <Typography
                variant="MDB"
                className={cn('tracking-[0] opacity-50', leadingNone)}
                color={c.white}
              >
                {suffixText}
              </Typography>
            )}
          </View>
        )}
      </View>
    </View>
  )
}
